import {api} from "@/lib/api/games/api";
import {IGetGamesRequest} from "@/lib/types/games/IGetGamesRequest";
import {IPageableResponse} from "@/lib/types/common/IPageableResponse";
import {IGame} from "@/lib/types/games/IGame";
import {IProducer} from "@/lib/types/games/IProducer";
import {IGameWithPersonalUserData} from "@/lib/types/games/IGameWithPersonalUserData";
import {IGetProvidersRequest} from "@/lib/types/games/IGetProvidersRequest";
import {IGetFreespinsGamesRequest} from "@/lib/types/games/IGetFreespinsGamesRequest";
import {ILobbyResponse} from "@/lib/types/games/ILobbyResponse";
import {ILobbyCategory} from "@/lib/types/games/ILobbyCategory";

export const gameApi = api.injectEndpoints({
    endpoints: build => ({
        getGames: build.infiniteQuery<IPageableResponse<IGame>, IGetGamesRequest, number>({
            infiniteQueryOptions: {
                initialPageParam: 0,
                getNextPageParam: (response, __, lastPageParam) => {
                    if (!response.totalPages || response.totalPages - 1 === lastPageParam) {
                        return undefined
                    }
                    return lastPageParam + 1
                },
            },
            query: (params) => ({
                url: `/api/v1/games/public/games${params.queryArg.isTop ? '/top100' : ''}`,
                method: 'get',
                params: {
                    ...params.queryArg,
                    isTop: undefined,
                    page: params.pageParam,
                }
            }),
        }),
        // AC-819: the enabled lobby categories of the player's GEO in their
        // effective display order — the lobby structure is managed in the
        // backoffice, not hardcoded here.
        getCategories: build.query<ILobbyCategory[], { country?: string }>({
            query: ({country}) => ({
                url: '/api/v1/games/public/categories',
                method: 'get',
                params: country ? {country} : undefined,
            }),
            keepUnusedDataFor: 300,
        }),
        // One request for the whole lobby: every category row + producers.
        // The catalogue is static, so keep it in the RTK cache for 5 minutes
        // instead of the 60s default. `country` (AC-819) resolves the per-GEO
        // configuration server-side.
        getLobby: build.query<ILobbyResponse, {
            categories: string[],
            size: number,
            country?: string,
            includeProducers?: boolean
        }>({
            query: ({categories, size, country, includeProducers = true}) => ({
                url: '/api/v1/games/public/games/batch',
                method: 'get',
                params: {
                    categories: categories.join(','),
                    size,
                    includeProducers,
                    ...(country ? {country} : {}),
                },
            }),
            keepUnusedDataFor: 300,
        }),
        getProviders: build.infiniteQuery<IPageableResponse<IProducer>, IGetProvidersRequest, number>({
            infiniteQueryOptions: {
                initialPageParam: 0,
                getNextPageParam: (response, __, lastPageParam) => {
                    if (!response.totalPages || response.totalPages - 1 === lastPageParam) {
                        return undefined
                    }
                    return lastPageParam + 1
                },
            },
            query: (params) => ({
                url: '/api/v1/games/public/producers',
                method: 'get',
                params: {
                    ...params.queryArg,
                    page: params.pageParam,
                }
            }),
        }),
        getGameById: build.query<IGameWithPersonalUserData, string>({
            query: (id) => ({
                url: '/api/v1/games/public/games/get',
                method: 'get',
                params: {id}
            }),
        }),
        // FS-eligible games for the free-spins game picker. Authed endpoint; the
        // server forces the provider + hasFreespins from yml config, so we only
        // ever send title/id/page/size/sort (empty title is omitted, not sent blank).
        getFreespinsGames: build.query<IPageableResponse<IGame>, IGetFreespinsGamesRequest>({
            query: ({title, id, page, size, sort}) => ({
                url: '/api/v1/games/private/freespins',
                method: 'get',
                params: {
                    page,
                    size,
                    ...(title && title.trim() ? {title: title.trim()} : {}),
                    ...(id ? {id} : {}),
                    ...(sort ? {sort} : {}),
                },
            }),
        }),
        // AC-929: the same catalogue page by page — the "Bonus games" page of a
        // Free Spins bonus is the whole of it, read as the player asks for more.
        getFreespinsGamesPages: build.infiniteQuery<IPageableResponse<IGame>, { size: number, title?: string }, number>({
            infiniteQueryOptions: {
                initialPageParam: 0,
                getNextPageParam: (response, __, lastPageParam) => {
                    if (!response.totalPages || response.totalPages - 1 === lastPageParam) {
                        return undefined
                    }
                    return lastPageParam + 1
                },
            },
            query: (params) => ({
                url: '/api/v1/games/private/freespins',
                method: 'get',
                params: {
                    size: params.queryArg.size,
                    page: params.pageParam,
                    ...(params.queryArg.title ? {title: params.queryArg.title} : {}),
                }
            }),
        }),
        // AC-924: an explicit set of games (a bonus' allowed games). The public
        // list endpoint filters by `ids` and serves such requests uncached; the
        // ids are sorted so the RTK cache key doesn't depend on their order.
        getGamesByIds: build.query<IPageableResponse<IGame>, string[]>({
            query: (ids) => ({
                url: '/api/v1/games/public/games',
                method: 'get',
                params: {ids: [...ids].sort().join(','), size: ids.length},
            }),
            keepUnusedDataFor: 300,
        }),
    })
})

export const {
    useGetGamesInfiniteQuery,
    useGetProvidersInfiniteQuery,
    useGetGameByIdQuery,
    useGetCategoriesQuery,
    useGetLobbyQuery,
    useGetFreespinsGamesQuery,
    useGetFreespinsGamesPagesInfiniteQuery,
    useGetGamesByIdsQuery,
} = gameApi;
